import { action, computed, makeObservable, observable } from 'mobx';

import { Region } from '@components/common/constants';
import { apiAuthCaller } from '@services/apiCaller';
import { webappClient } from '@lib/bugsnag';
import type { IRootStore } from '@stores/index';

import type { IInvoiceDetail } from './types';
import { selectUnpaidInvoice } from './unpaidInvoice';

/**
 * Holds the customer's invoices and derives the unpaid-invoice signal that drives the app-wide
 * payment-failed banner and the billing-screen retry alert. Composed by BillingStore.
 */
export class InvoicesStore {
  @observable public invoices: IInvoiceDetail[] = [];

  constructor(private readonly rootStore: IRootStore) {
    makeObservable(this);
  }

  @action private setInvoices(invoices: IInvoiceDetail[]) {
    this.invoices = invoices;
  }

  @action.bound public reset() {
    this.invoices = [];
  }

  @action.bound
  public async getInvoices() {
    const {
      featuresStore,
      organizationStore: { id: orgId },
    } = this.rootStore;
    // No admin gate: the invoices route only requires org membership, and non-admin members need
    // the unpaid-invoice signal too (they get an FYI banner asking an admin to act).
    if (!orgId || !featuresStore.has('BILLING_PAGE')) {
      return;
    }
    try {
      const { data } = await apiAuthCaller({
        nonWorkspaceRoute: true,
        region: Region.US,
      }).get<IInvoiceDetail[]>(`organizations/${orgId}/invoices`);
      this.setInvoices(data);
    } catch (error) {
      webappClient.notify(error as Error);
    }
  }

  // The alert/banner signal. Logic lives in a leaf module so it is testable without loading the
  // store graph; see selectUnpaidInvoice for why this is the latest invoice and not any open one.
  @computed get unpaidInvoice(): IInvoiceDetail | undefined {
    return selectUnpaidInvoice(this.invoices);
  }

  @computed get hasUnpaidInvoice(): boolean {
    return this.unpaidInvoice !== undefined;
  }
}
